import { z } from "zod";
import { TRIP_TYPES } from "../types";
import { isIsoDate } from "../utils";
import {
  ATTRIBUTION_FIELDS,
  CONTACT_FIELDS,
  NOTES_FIELDS,
  ORIGIN_PATH_FIELD,
  TRAVELLER_FIELDS,
  requireChildAges,
} from "./inquiryFields";
import {
  CUSTOM_TRIP_ID_MAX_LENGTH,
  DESTINATION_SLUG_MAX_LENGTH,
  DURATION_DAYS_MAX,
  DURATION_DAYS_MIN,
  INQUIRY_MESSAGES as M,
  blankNumberToUndefined,
  blankToUndefined,
} from "../inquiryRules";

// ============================================================================
// CUSTOM INQUIRY SCHEMA (poptávka BEZ zájezdu — řez 07, vize F2)
// ============================================================================

/** Volitelné kalendářně platné ISO datum (`2027-13-45` je tvarem v pořádku, datem ne). */
const optionalIsoDate = z.preprocess(
  blankToUndefined,
  z
    .string()
    .refine((value) => isIsoDate(value), M.dateInvalid)
    .optional(),
);

/**
 * Poptávka bez konkrétního zájezdu. Konzumenti jsou dva — formulář na webu
 * (validátor `apps/web/src/lib/inquiryValidation.ts` s paritou proti tomuhle schématu,
 * hlášky se vykreslují doslova pod poli) a `POST /api/inquiries/custom`
 * (hláška 400) — takže všechna znění jsou česká.
 *
 * Povinné je kontaktní spojení a **aspoň jeden z údajů kam nebo kdy**: bez obojího by
 * v systému vznikl záznam, se kterým obchodník nemá co dělat. „Kam" nemá volný text —
 * jediným domovem destinace je slug, který se v API rezolvuje na relaci `leads.destinationId`;
 * co zákazník chce nad rámec číselníku, patří do poznámky.
 *
 * Pole shodná s poptávkou k zájezdu (kontakt, cestující, poznámka, atribuce) mají jediný
 * domov v `inquiryFields.ts` — spready stojí přesně tam, kde ta pole stála doslovně, protože
 * pořadí klíčů určuje pořadí chyb a tím i skok fokusu na webu.
 */
export const customInquirySchema = z
  .object({
    ...CONTACT_FIELDS,
    /** Slug publikované destinace; existenci ověřuje až API (číselník je na serveru). */
    destinationSlug: z.preprocess(
      blankToUndefined,
      z.string().max(DESTINATION_SLUG_MAX_LENGTH).optional(),
    ),
    dateFrom: optionalIsoDate,
    dateTo: optionalIsoDate,
    durationDays: z.preprocess(
      blankNumberToUndefined,
      z
        .number({ invalid_type_error: M.durationType })
        .int(M.durationInt)
        .min(DURATION_DAYS_MIN, M.durationRange)
        .max(DURATION_DAYS_MAX, M.durationRange)
        .optional(),
    ),
    ...TRAVELLER_FIELDS,
    /**
     * KATALOGOVÁ osa (`guided|private|resort`), ne osa profilu leadu
     * (`fit|guided|stay|service`) — stejný klíč, jiný číselník. Zákazník tu volí produkt
     * z nabídky, ne charakteristiku sebe sama.
     */
    tripType: z.preprocess(blankToUndefined, z.enum(TRIP_TYPES).optional()),
    ...NOTES_FIELDS,
    /** Veřejná cesta původu — sdílený kus, viz `ORIGIN_PATH_FIELD`. */
    originPath: ORIGIN_PATH_FIELD,
    /** Kontext z detailu zájezdu. Neznámé id API zahodí, poptávku NEODMÍTNE. */
    tripId: z.preprocess(blankToUndefined, z.string().max(CUSTOM_TRIP_ID_MAX_LENGTH).optional()),
    ...ATTRIBUTION_FIELDS,
  })
  .superRefine((data, ctx) => {
    if (!data.destinationSlug && !data.dateFrom && !data.dateTo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: M.whereOrWhenRequired,
        path: ["destinationSlug"],
      });
    }
    if (data.dateFrom && data.dateTo && data.dateTo < data.dateFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: M.customDateOrder,
        path: ["dateTo"],
      });
    }
    requireChildAges(ctx, data);
  });

export type CustomInquiryInput = z.infer<typeof customInquirySchema>;
